import React, { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { 
  Flower, Plus, Trash, Warning, CheckCircle, ShieldCheck, Info, X, File, UploadSimple
} from '@phosphor-icons/react';

export function FragranceInputSection({ fragrance, setFragrance }) {
  const handleToggle = (val) => {
    setFragrance({ ...fragrance, contains_fragrance: val });
  };

  const handleDisclosedToggle = (val) => {
    setFragrance({ ...fragrance, fragrance_disclosed: val });
  };

  const addComponent = () => {
    setFragrance({
      ...fragrance,
      fragrance_components: [
        ...(fragrance.fragrance_components || []),
        { _id: crypto.randomUUID(), inci_name: '', cas_number: '', concentration: '' }
      ]
    });
  };

  const removeComponent = (idx) => {
    const updated = [...fragrance.fragrance_components];
    updated.splice(idx, 1);
    setFragrance({ ...fragrance, fragrance_components: updated });
  };

  const updateComponent = (idx, field, value) => {
    const updated = [...fragrance.fragrance_components];
    updated[idx] = { ...updated[idx], [field]: value };
    setFragrance({ ...fragrance, fragrance_components: updated });
  };

  return (
    <div className="card-flat p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Flower size={22} className="text-[#0B2447]" weight="duotone" />
          <h2 className="text-lg font-semibold text-[#111827]">Fragrance / Parfum</h2>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="fragrance-toggle" className="text-sm text-[#4B5563]">
            Contains fragrance
          </Label>
          <Switch
            id="fragrance-toggle"
            checked={fragrance.contains_fragrance}
            onCheckedChange={handleToggle}
            data-testid="fragrance-toggle"
          />
        </div>
      </div>

      {!fragrance.contains_fragrance && (
        <p className="text-sm text-[#4B5563]">Toggle on if this product contains any fragrance or parfum.</p>
      )}

      {fragrance.contains_fragrance && (
        <div className="space-y-5 mt-2">
          {/* Fragrance % in product */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-sm font-semibold">Fragrance % in finished product</Label>
              <Input
                type="number" step="0.01" min="0" max="100"
                value={fragrance.fragrance_pct_in_product}
                onChange={(e) => setFragrance({ ...fragrance, fragrance_pct_in_product: parseFloat(e.target.value) || 0 })}
                data-testid="fragrance-pct-input"
                className="h-10 rounded-sm text-sm"
                placeholder="e.g., 1.5"
              />
            </div>
          </div>

          {/* Disclosure toggle */}
          <div className="p-4 bg-[#F9FAFB] border border-gray-200 rounded-sm">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-[#111827]">Fragrance composition disclosure</p>
                <p className="text-xs text-[#4B5563]">
                  {fragrance.fragrance_disclosed
                    ? 'You will provide the fragrance breakdown (preferred for full assessment)'
                    : 'Fragrance composition is supplier confidential'}
                </p>
              </div>
              <Switch
                checked={fragrance.fragrance_disclosed}
                onCheckedChange={handleDisclosedToggle}
                data-testid="fragrance-disclosed-toggle"
              />
            </div>

            {fragrance.fragrance_disclosed ? (
              /* Option 1: Upload/enter composition */
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-3">
                  Fragrance Components (% in fragrance compound)
                </p>
                <div className="space-y-2">
                  {(fragrance.fragrance_components || []).map((comp, idx) => (
                    <div key={comp._id || idx} className="flex gap-2 items-center">
                      <Input
                        placeholder="INCI Name *"
                        value={comp.inci_name}
                        onChange={(e) => updateComponent(idx, 'inci_name', e.target.value)}
                        data-testid={`fragrance-comp-name-${idx}`}
                        className="h-9 text-sm rounded-sm flex-1"
                      />
                      <Input
                        placeholder="CAS"
                        value={comp.cas_number}
                        onChange={(e) => updateComponent(idx, 'cas_number', e.target.value)}
                        data-testid={`fragrance-comp-cas-${idx}`}
                        className="h-9 text-sm rounded-sm w-28"
                      />
                      <Input
                        type="number" step="0.01" min="0" max="100" placeholder="% *"
                        value={comp.concentration}
                        onChange={(e) => updateComponent(idx, 'concentration', e.target.value)}
                        data-testid={`fragrance-comp-pct-${idx}`}
                        className="h-9 text-sm rounded-sm w-20 tabular-nums"
                      />
                      <button
                        type="button"
                        onClick={() => removeComponent(idx)}
                        className="p-1.5 text-[#4B5563] hover:text-[#9B1C1C] hover:bg-red-50 rounded"
                        data-testid={`fragrance-comp-remove-${idx}`}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button" variant="outline" onClick={addComponent}
                  className="mt-3 rounded-sm text-sm h-8"
                  data-testid="fragrance-add-component-btn"
                >
                  <Plus size={14} className="mr-1" /> Add Component
                </Button>
              </div>
            ) : (
              /* Option 2: Undisclosed */
              <div className="space-y-3">
                <div className="flex items-start gap-2 p-3 bg-[#FDF6B2] border border-[#FACA15] rounded-sm">
                  <Warning size={18} className="text-[#723B13] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#723B13]">
                    Without compositional data, sensitization risk cannot be fully assessed.
                    Please upload an IFRA certificate if available.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="ifra-cert"
                    checked={fragrance.ifra_certificate_uploaded}
                    onChange={(e) => setFragrance({ ...fragrance, ifra_certificate_uploaded: e.target.checked })}
                    data-testid="ifra-cert-checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-[#0B2447] focus:ring-[#2563EB]"
                  />
                  <Label htmlFor="ifra-cert" className="text-sm text-[#111827]">
                    IFRA Certificate provided (uploaded via Supporting Documents)
                  </Label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============== FRAGRANCE RESULTS DISPLAY ============== */

function SensRiskBadge({ risk }) {
  const cfg = {
    minimal:  { bg: 'bg-[#DEF7EC]', text: 'text-[#03543F]', label: 'Minimal' },
    low:      { bg: 'bg-[#DEF7EC]', text: 'text-[#03543F]', label: 'Low' },
    moderate: { bg: 'bg-[#FDF6B2]', text: 'text-[#723B13]', label: 'Moderate' },
    elevated: { bg: 'bg-[#FDE8E8]', text: 'text-[#9B1C1C]', label: 'Elevated' },
    unknown:  { bg: 'bg-[#F3F4F6]', text: 'text-[#4B5563]', label: 'Unknown' },
  };
  const c = cfg[risk] || cfg.unknown;
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm ${c.bg} ${c.text}`}>{c.label}</span>;
}

function IFRABadge({ status }) {
  const cfg = {
    compliant:    { cls: 'status-acceptable', label: 'IFRA Compliant' },
    near_limit:   { cls: 'status-needs-review', label: 'Near IFRA Limit' },
    exceeds:      { cls: 'status-not-acceptable', label: 'Exceeds IFRA' },
    banned:       { cls: 'status-not-acceptable', label: 'IFRA Banned' },
    no_limit_data:{ cls: 'bg-[#F3F4F6] text-[#4B5563] border border-gray-200', label: 'No Limit Data' },
    'N/A':        { cls: 'bg-[#F3F4F6] text-[#4B5563] border border-gray-200', label: 'N/A' },
  };
  const c = cfg[status] || cfg['N/A'];
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm ${c.cls}`}>{c.label}</span>;
}

export function FragranceResultsSection({ fragranceResults, mode }) {
  if (!fragranceResults || !fragranceResults.active) return null;

  const { disclosed, summary, warnings, component_results, ifra_certificate_uploaded, fragrance_pct_in_product } = fragranceResults;

  return (
    <div className="card-flat overflow-hidden mb-8" data-testid="fragrance-results-section">
      <div className="p-4 border-b border-gray-200 flex items-center gap-3">
        <Flower size={20} className="text-[#0B2447]" weight="duotone" />
        <div>
          <h2 className="text-lg font-semibold text-[#111827]">Fragrance Safety & Allergen Assessment</h2>
          <p className="text-sm text-[#4B5563]">
            Fragrance at {fragrance_pct_in_product}% in product
            {!disclosed && ' — composition undisclosed'}
          </p>
        </div>
      </div>

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div className="p-4 bg-[#FDF6B2] border-b border-[#FACA15]">
          {warnings.map((w) => (
            <p key={w} className="text-sm text-[#723B13] flex items-center gap-2 mb-1 last:mb-0">
              <Warning size={14} weight="bold" className="flex-shrink-0" /> {w}
            </p>
          ))}
        </div>
      )}

      {/* Summary */}
      <div className="p-4 border-b border-gray-200 bg-[#F9FAFB]">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Allergens</p>
            <p className="text-xl font-black text-[#111827] tabular-nums">{summary.total_allergens || 0}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Sensitization Risk</p>
            <div className="mt-1"><SensRiskBadge risk={summary.sensitization_risk} /></div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">IFRA Status</p>
            <div className="mt-1">
              {summary.data_gap ? (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-[#F3F4F6] text-[#4B5563]">
                  {ifra_certificate_uploaded ? 'Cert. Provided' : 'Unverified'}
                </span>
              ) : summary.ifra_compliant ? (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-sm status-acceptable">Compliant</span>
              ) : (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-sm status-not-acceptable">{summary.ifra_violations} Violation(s)</span>
              )}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">EU Allergens</p>
            <p className="text-sm font-semibold mt-1">
              {summary.eu_allergens_present ? (
                <span className="text-[#723B13]">Present</span>
              ) : summary.data_gap ? (
                <span className="text-[#4B5563]">Unknown</span>
              ) : (
                <span className="text-[#0E9F6E]">None detected</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Component table */}
      {component_results && component_results.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-gray-200">
                <th className="text-left font-semibold text-[#111827] px-4 py-3">Ingredient</th>
                <th className="text-left font-semibold text-[#111827] px-4 py-3">CAS #</th>
                <th className="text-right font-semibold text-[#111827] px-4 py-3">% in Product</th>
                {mode === 'full' && <th className="text-right font-semibold text-[#111827] px-4 py-3">SED</th>}
                <th className="text-left font-semibold text-[#111827] px-4 py-3">EU Allergen</th>
                <th className="text-left font-semibold text-[#111827] px-4 py-3">Sensitization</th>
                {mode === 'full' && <th className="text-left font-semibold text-[#111827] px-4 py-3">IFRA</th>}
                <th className="text-left font-semibold text-[#111827] px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {component_results.map((cr) => (
                <tr key={cr.cas_number || cr.ingredient_name} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-[#111827]">{cr.ingredient_name}</td>
                  <td className="px-4 py-3 text-[#4B5563] tabular-nums">{cr.cas_number || '-'}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{cr.pct_in_product?.toFixed(4)}</td>
                  {mode === 'full' && <td className="px-4 py-3 text-right tabular-nums">{cr.sed?.toFixed(6) || '-'}</td>}
                  <td className="px-4 py-3">
                    {cr.eu_allergen ? (
                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded-sm bg-[#FDF6B2] text-[#723B13]">EU Listed</span>
                    ) : (
                      <span className="text-[#4B5563]">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold ${
                      (cr.sensitization_potential || cr.sensitization_flag) === 'Strong' ? 'text-[#9B1C1C]' :
                      (cr.sensitization_potential || cr.sensitization_flag) === 'Moderate' ? 'text-[#723B13]' :
                      'text-[#4B5563]'
                    }`}>
                      {cr.sensitization_potential || cr.sensitization_flag || '-'}
                    </span>
                  </td>
                  {mode === 'full' && <td className="px-4 py-3"><IFRABadge status={cr.ifra_status} /></td>}
                  <td className="px-4 py-3 text-xs text-[#4B5563]">{cr.regulatory_notes || cr.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Disclaimer */}
      <div className="p-4 bg-[#F3F4F6] border-t border-gray-200">
        <p className="text-xs text-[#4B5563]">
          Fragrance assessments are limited by data availability. Full safety conclusions require complete compositional disclosure.
          {!disclosed && ' This assessment is based on limited information — results should be interpreted with caution.'}
        </p>
      </div>
    </div>
  );
}
